import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { AppRoutes } from './app/routes';
import { Providers } from './app/providers';
import './styles/index.css';
import { detectHostKind, probeHostStore } from './features/storefront/host-mode';
import { setHostStore } from './features/storefront/store-paths';

// O portal do cliente é outra aplicação: providers, cache e sessão próprios, nada da área da loja.
const PortalApp = lazy(() => import('./features/portal/portal-app'));
const StoreApp = lazy(() => import('./features/storefront/store-app'));
const HostStoreNotFound = lazy(() => import('./features/storefront/host-not-found').then((m) => ({ default: m.HostStoreNotFound })));
const isStoreUrl = /^\/loja\/[^/]+/.test(window.location.pathname);
const isPortal = /^\/portal(\/|$)/.test(window.location.pathname);
// landing e páginas legais trazem o próprio link "Ir para o conteúdo": sem duplicar o do painel
const hasOwnSkipLink = /^\/(produto|privacidade|termos)(\/|$)/.test(window.location.pathname);

function mount(kind: 'store' | 'host' | 'host-missing' | 'platform') {
  const isStore = kind === 'store' || kind === 'host';
  createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {kind === 'host-missing' ? (
      <Suspense fallback={null}>
        <HostStoreNotFound />
      </Suspense>
    ) : isStore ? (
      <Suspense fallback={null}>
        <StoreApp />
      </Suspense>
    ) : isPortal ? (
      <Suspense fallback={null}>
        <PortalApp />
      </Suspense>
    ) : (
      <>
        {!hasOwnSkipLink && (
          <a href="#conteudo" className="sr-only z-[100] rounded-control bg-card px-3 py-2 text-sm focus:not-sr-only focus:fixed focus:top-2 focus:left-2">
            Pular para o conteúdo
          </a>
        )}
        <Providers>
          <AppRoutes />
        </Providers>
      </>
    )}
  </StrictMode>,
);
}

/**
 * Domínio próprio: o nginx marca o HTML (`bf-host-kind`); em dev `*.localhost` é sondado na API. Em host de loja
 * NUNCA se monta painel, portal nem landing: ou é a loja na raiz, ou "Loja não encontrada".
 */
async function boot() {
  const kind = detectHostKind();
  if (kind === 'platform') return mount(isStoreUrl ? 'store' : 'platform');
  const slug = await probeHostStore();
  if (slug) {
    setHostStore(slug);
    return mount('host');
  }
  // dev: `*.localhost` sem loja cai no comportamento normal; host de loja em produção mostra a página neutra
  return mount(kind === 'store' ? 'host-missing' : isStoreUrl ? 'store' : 'platform');
}

void boot();
